/* eslint-disable max-len */
/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import i18n from 'i18next';
import PropTypes from 'prop-types';

import { ListBody, ListEmpty, ListHeader } from '@/exceed';
import { NestedRows, DraggableListItem, DraggableListItemPlaceholder } from '@/nestedRows';

import { Loading } from 'uikit/loaders';

import VideosAdminSearchContainer from '../containers/videosAdminSearchContainer';

import VideosAdminListDragAndDropContent from './videosAdminListDragAndDropContent';
import VideosAdminListDragAndDropKebabMenu from './videosAdminListDragAndDropKebabMenu';
import VideosAdminSearchResultItem from './videosAdminSearchResultItem';
import VideoAdminsListPlaceholderContent from './videoAdminsListPlaceholderContent';

import getVideoMenuItems from '../helpers/getVideoMenuItems';
import getVideoActionMenuItems from '../helpers/getVideoActionMenuItems';

import styles from '../styles/videosAdminContent.module.scss';

const ListItem = (props) => <VideosAdminSearchResultItem {...props} />;

const VideosAdminContent = ({
  isVideoHomeEditor,
  isOnDemandHomePage,
  isInLiveMode,
  shouldDisableDrag,
  shouldDisableTopLevelCreation,
  shouldShowListView,
  shouldShowEmptyState,
  listEmptyComponent,
  shouldHideSearchBar,
  currentTreeContent,
  breadcrumbs,
  lastBreadcrumbMenuItems,
  onDragEnd,
  onKebabMenuItemClick,
  onActionItemClick,
  onContentActionClick,
  onToggleButtonsChange,
  listHeaderAddMenuItems,
  shouldShowSearchResults,
  shouldShowLoadingWheel,
  shouldShowListAddMenu,
  shouldShowBreadCrumbs,
  listHeaderTitle,
  listHeaderColumns,
  listHeaderToggleButtons,
  listHeaderToggleButtonsValue,
  isComposeListDisabled,
  params,
  setIsShowingLoadingWheel,
}) => (
  <div className={styles.element}>
    {!shouldHideSearchBar && (
      <VideosAdminSearchContainer
        isOnDemandHomePage={isOnDemandHomePage}
        setIsShowingLoadingWheel={setIsShowingLoadingWheel}
      />
    )}
    {shouldShowLoadingWheel ? (
      <div className={styles.loading}>
        <Loading />
      </div>
    ) : (
      <>
        <ListHeader
          className={styles.listHeader}
          columns={listHeaderColumns}
          addMenuItems={listHeaderAddMenuItems}
          hasAddMenu={shouldShowListAddMenu}
          title={listHeaderTitle}
          nameText={i18n.t('name')}
          hasBreadcrumbs={shouldShowBreadCrumbs}
          breadcrumbs={breadcrumbs}
          lastBreadcrumbMenuItems={lastBreadcrumbMenuItems}
          hasToggleButtons={Boolean(listHeaderToggleButtons.length)}
          onToggleButtonsChange={onToggleButtonsChange}
          toggleButtons={listHeaderToggleButtons}
          toggleButtonsColor="brand"
          toggleButtonsValue={listHeaderToggleButtonsValue}
          isDisabled={shouldDisableTopLevelCreation || isComposeListDisabled}
        />
        {shouldShowListView && (
          <div className={styles.rows}>
            <NestedRows
              data={currentTreeContent}
              shouldPreventTransform
              containerHeight="auto"
              onDragEnd={onDragEnd}
              isDragEnabled={!shouldDisableDrag}
              isNestingEnabled={false}
              offsetPerLevel={32}
              renderComponent={(props) => {
                const hasEditorPermission =
                  props.item.data.metadata?.has_editor_permission ||
                  props.item.data.has_editor_permission;
                const isKebabDisabled = !hasEditorPermission;
                const isAddButtonDisabled = isOnDemandHomePage
                  ? !isVideoHomeEditor
                  : !hasEditorPermission;
                return (
                  <DraggableListItem
                    {...props}
                    isDragDisabled={shouldDisableDrag}
                    dragTooltipText={i18n.t('drag-tooltip')}
                    addButtonTooltipText={i18n.t('add-tooltip')}
                    kebabTooltipText={i18n.t('kebab-tooltip')}
                    isAddButtonDisabled={isAddButtonDisabled}
                    isKebabDisabled={isKebabDisabled}
                    kebabMenuItems={getVideoMenuItems({
                      item: props.item,
                      onKebabMenuItemClick,
                      params,
                    })}
                    actionMenuItems={getVideoActionMenuItems({
                      item: props.item,
                      onActionItemClick,
                    })}
                    onContentActionClick={onContentActionClick}
                    components={{
                      Content: VideosAdminListDragAndDropContent,
                      ToggleExpandButton: () => null,
                      ...(isInLiveMode && {
                        Actions: () => null,
                        DragHandle: () => null,
                      }),
                    }}
                  />
                );
              }}
              renderCustomPlaceholder={(props) => (
                <DraggableListItemPlaceholder
                  {...props}
                  components={{ Content: VideoAdminsListPlaceholderContent }}
                />
              )}
            />
          </div>
        )}
        {shouldShowSearchResults && (
          <ListBody
            data={currentTreeContent.items}
            emptyComponent={
              <ListEmpty
                className={styles.searchEmpty}
                title={i18n.t('no-results-found')}
                body={i18n.t('modify-your-search')}
                isButtonDisabled={shouldDisableTopLevelCreation}
              />
            }
            isUngrouped
            ListElement="ul"
            ListItem={({ item }) =>
              ListItem({
                components: { KebabMenu: VideosAdminListDragAndDropKebabMenu },
                item,
                onKebabMenuItemClick,
              })
            }
          />
        )}
        {shouldShowEmptyState && listEmptyComponent}
      </>
    )}
  </div>
);

VideosAdminContent.defaultProps = {
  isVideoHomeEditor: false,
  isOnDemandHomePage: false,
  isInLiveMode: false,
  shouldShowListView: false,
  shouldDisableTopLevelCreation: false,
  shouldShowEmptyState: false,
  listEmptyComponent: null,
  currentTreeContent: {},
  searchResults: {},
  onDragEnd: () => {},
  onKebabMenuItemClick: () => {},
  onActionItemClick: () => {},
  listHeaderAddMenuItems: [],
  listHeaderTitle: '',
  shouldHideSearchBar: false,
  shouldShowSearchResults: false,
  shouldShowLoadingWheel: true,
  shouldShowListAddMenu: true,
  shouldShowBreadCrumbs: false,
  isComposeListDisabled: false,
};

VideosAdminContent.propTypes = {
  isVideoHomeEditor: PropTypes.bool,
  isOnDemandHomePage: PropTypes.bool,
  isInLiveMode: PropTypes.bool,
  shouldShowListView: PropTypes.bool,
  shouldDisableTopLevelCreation: PropTypes.bool,
  shouldShowEmptyState: PropTypes.bool,
  listEmptyComponent: PropTypes.node,
  currentTreeContent: PropTypes.shape({
    rootId: PropTypes.string,
    items: PropTypes.oneOfType([
      PropTypes.objectOf(
        PropTypes.shape({
          id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          children: PropTypes.arrayOf(PropTypes.number),
          isExpanded: PropTypes.bool,
        }),
      ),
      PropTypes.array,
    ]),
  }),
  searchResults: PropTypes.shape({}),
  onDragEnd: PropTypes.func,
  onKebabMenuItemClick: PropTypes.func,
  onActionItemClick: PropTypes.func,
  listHeaderAddMenuItems: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.string,
      key: PropTypes.string,
      onClick: PropTypes.func,
    }),
  ),
  shouldShowSearchResults: PropTypes.bool,
  shouldShowLoadingWheel: PropTypes.bool,
  shouldShowListAddMenu: PropTypes.bool,
  shouldShowBreadCrumbs: PropTypes.bool,
  shouldHideSearchBar: PropTypes.bool,
  listHeaderTitle: PropTypes.string,
  isComposeListDisabled: PropTypes.bool,
};

export default VideosAdminContent;
